import React from 'react';
import LegalPage from '@/components/LegalPage';
import usePageTitle from '@/hooks/usePageTitle';

const IPPolicy = () => {
  usePageTitle('IP Policy');
  return (
  <LegalPage
    kicker="Legal"
    title="Copyright &amp; Intellectual Property Policy"
    lastUpdated="02 July 2026"
  >
    <p>
      This policy explains who owns what when a campaign, case study, video,
      creative asset or supporting document is submitted to{' '}
      <strong>The AdAura Awards 2026</strong>, and the limited rights the
      entrant grants to Marketing Mind in exchange for participation, judging
      and public recognition.
    </p>

    <h2>1. Ownership stays with the entrant</h2>
    <ul>
      <li>You retain <strong>full copyright and ownership</strong> of every creative work, campaign film, case-study deck, image, script, jingle, artwork, code and dataset you submit.</li>
      <li>Nothing in the entry process transfers ownership, moral rights, or any exclusive right to Marketing Mind or its affiliates.</li>
    </ul>

    <h2>2. Limited licence granted to Marketing Mind</h2>
    <p>
      By submitting an entry, the nominator grants Marketing Mind a{' '}
      <strong>non-exclusive, royalty-free, worldwide, sub-licensable licence</strong>{' '}
      to use the submitted material for the purposes listed below:
    </p>
    <ul>
      <li><strong>Judging</strong> — displaying the entry to jurors inside the Jury Chamber with all identifying credits stripped (blind judging).</li>
      <li><strong>Award-night reels &amp; ceremony</strong> — screening excerpts of shortlisted and winning work on stage and in supporting AV.</li>
      <li><strong>Editorial &amp; case-study library</strong> — publishing entry excerpts, credits and commentary on adauraawards.com, in the AdAura report, and in Marketing Mind editorial coverage.</li>
      <li><strong>Social media &amp; PR</strong> — promoting shortlisted, finalist and winning work across Marketing Mind’s owned social channels, newsletters and press releases.</li>
      <li><strong>Winners’ archive</strong> — perpetual display of winning entries with full credits on the AdAura winners’ gallery.</li>
    </ul>
    <p>
      Marketing Mind will always credit named contributors (brand, agency,
      production house, freelancers, jurors) as declared in the entry form.
    </p>

    <h2>3. Restricted uses</h2>
    <ul>
      <li>We will <strong>not</strong> re-license, re-sell, or sub-license entry material to a third-party brand for commercial promotion of that brand’s products or services without your written consent.</li>
      <li>We will <strong>not</strong> use entries to train generative AI models.</li>
      <li>Non-shortlisted entries are used strictly for the judging process and are not published without your written consent.</li>
    </ul>

    <h2>4. Third-party rights &amp; clearances</h2>
    <p>
      By submitting, the nominator represents and warrants that:
    </p>
    <ul>
      <li>They have secured all necessary clearances for the material — including music, footage, talent, model releases, voice-over artistes, brand/product IP and any celebrity association.</li>
      <li>The submitted work does not infringe any third-party copyright, trademark, publicity right, or confidentiality obligation.</li>
      <li>Where certain clearances restrict Marketing Mind’s permitted use above (for example, a music track licensed only for broadcast), the nominator will flag the restriction in the entry form so we can restrict use accordingly.</li>
      <li>The nominator will indemnify Marketing Mind against any third-party claim arising from a breach of this warranty.</li>
    </ul>

    <h2>5. Confidential information</h2>
    <p>
      Business results, budgets, media spend and internal data shared for the
      purpose of scoring are treated as confidential. Jurors are bound by our{' '}
      <a href="/juror-code-of-conduct">Juror Code of Conduct</a> and cannot copy,
      reproduce, share or repurpose such data. Marketing Mind will publish
      quantitative results only with the entrant’s explicit consent, and will
      round or redact figures where requested.
    </p>

    <h2>6. AdAura marks</h2>
    <p>
      “AdAura”, the AdAura wordmark, the AdAura Awards trophy device, and
      “Marketing Mind” are marks of Digi Giant LLP. Shortlisted and winning
      entrants receive a licensed “AdAura Awards 2026” badge for use on
      their own case studies, website, LinkedIn and portfolio. Any other
      commercial use of these marks requires written permission.
    </p>

    <h2>7. Copyright infringement takedown (DMCA-style)</h2>
    <p>
      If you believe any material on adauraawards.com infringes your copyright,
      write to{' '}
      <a href="mailto:awards@marketingmind.co">awards@marketingmind.co</a>{' '}
      with the subject <em>“Copyright takedown”</em> and include:
    </p>
    <ul>
      <li>A description of the copyrighted work you claim has been infringed.</li>
      <li>The exact URL(s) on our site where the material appears.</li>
      <li>Your contact details and a good-faith statement that the use is unauthorised.</li>
      <li>An electronic signature.</li>
    </ul>
    <p>
      We investigate within five business days and remove infringing material
      pending review.
    </p>

    <h2>8. Post-award requests</h2>
    <p>
      An entrant may request Marketing Mind to remove specific entry material
      from the public site at any time, on genuine confidentiality or IP
      grounds. We honour reasonable requests within 30 days. The record of
      participation, shortlisting or winning remains part of the AdAura
      editorial archive.
    </p>
  </LegalPage>
  );
};

export default IPPolicy;
